import type { Coordinates } from '$lib/components/map/types';
import type { WayPoint } from '$lib/components/search/itinerary/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { SupportedLocale } from '@viamichelin/business';
import type { LayoutLoadEvent } from './$types';
import type { LayoutLoadEvent as LayoutDetailsLoadEvent } from './(details)/$types';
import type { PageLoadEvent } from './results/[...addresses]/$types';
import type { WaypointsInfo } from './types';
import {
	type LocalizedHierarchyFragment$data,
	type SearchItineraryAddressesInput,
	searchItineraryAddressesStore,
} from '$houdini';
import { getAddressFromLocalizedHierarchies } from '$lib/address/hierarchy';
import { filterUndefined } from '$lib/utils/predicates';
import { error } from '@sveltejs/kit';
import { get } from 'svelte/store';

type LoadEvent = PageLoadEvent | LayoutDetailsLoadEvent | LayoutLoadEvent;

const ADDRESS_SEPARATOR = '-to-';
const MIN_WAYPOINTS_REQUIRED = 2;

function getSearchItineraryAddressInput(
	addressesFromRouteParam: string | undefined,
	itineraryFromSearchParam: WayPoint[] | undefined,
): SearchItineraryAddressesInput | undefined {
	const addressesLabels = addressesFromRouteParam?.split(ADDRESS_SEPARATOR);
	const addressesCoordinates
		= itineraryFromSearchParam?.length === MIN_WAYPOINTS_REQUIRED
			&& itineraryFromSearchParam.every((wp): wp is WayPoint => wp.addressType === 'CITY')
			? itineraryFromSearchParam.map(wp => wp.coordinates)
			: undefined;

	if (addressesLabels && addressesLabels.length === MIN_WAYPOINTS_REQUIRED) {
		const [from, to] = addressesLabels;

		if (!from || !to) {
			// TODO: Put 400 error when we have a proper error page
			return error(500, 'Invalid addresses format');
		}

		return {
			arrival: {
				query: to,
			},
			departure: {
				query: from,
			},
		};
	}

	if (addressesCoordinates) {
		return {
			arrival: {
				query: '',
				coordinates: addressesCoordinates[addressesCoordinates.length - 1] as Coordinates,
			},
			departure: {
				query: '',
				coordinates: addressesCoordinates[0] as Coordinates,
			},
		};
	}

	return undefined;
}

/**
 * Get the itinerary labels from the search params the event param or the search iti address result
 *
 * We want to get the labels from:
 * - the search params if the user has entered a search query in the search bar
 * - the search iti result if we are in a vanity url
 */
function getItineraryLabels(
	addressesFromRouteParam: string | undefined,
	itineraryFromSearchParam: WayPoint[] | undefined,
	departureHierarchies: LocalizedHierarchyFragment$data[],
	arrivalHierarchies: LocalizedHierarchyFragment$data[],
	locale: SupportedLocale,
) {
	const addressesFromSearchParams = itineraryFromSearchParam?.map(wp => wp.label);
	const addressesFromParam = addressesFromRouteParam?.split(ADDRESS_SEPARATOR);

	if (addressesFromSearchParams && addressesFromSearchParams.every(filterUndefined)) {
		return addressesFromSearchParams;
	}

	const [from, to] = addressesFromParam ?? [];

	return [
		getAddressFromLocalizedHierarchies(departureHierarchies, locale).city ?? from,
		getAddressFromLocalizedHierarchies(arrivalHierarchies, locale).city ?? to,
	];
}

export function getWaypointsInfoFromSearchParams(searchParams: SearchParams): WaypointsInfo | undefined {
	const waypoints = searchParams.itinerary ?? [];

	if (waypoints.length < MIN_WAYPOINTS_REQUIRED) {
		return;
	}

	const firstWaypoint = waypoints[0];
	const lastWaypoint = waypoints[waypoints.length - 1];

	const departure = {
		name: firstWaypoint?.label ?? '',
		countryCode: firstWaypoint?.countryCode ?? '',
		region: firstWaypoint?.region,
		zipCode: firstWaypoint?.zipCode,
	};

	const arrival = {
		name: lastWaypoint?.label ?? '',
		countryCode: lastWaypoint?.countryCode ?? '',
		region: lastWaypoint?.region,
		zipCode: lastWaypoint?.zipCode,
	};

	return {
		waypoints,
		departure,
		arrival,
	};
}

async function getWaypointsInfoFromSearchItineraryAddresses(event: LoadEvent, addressesFromRouteParam: string | undefined, itineraryFromSearchParam: WayPoint[] | undefined, locale: SupportedLocale): Promise<WaypointsInfo | undefined> {
	const searchAddressesInput = getSearchItineraryAddressInput(
		addressesFromRouteParam,
		itineraryFromSearchParam,
	);

	if (!searchAddressesInput) {
		return;
	}

	// eslint-disable-next-line new-cap
	const searchItineraryAddresses = new searchItineraryAddressesStore();
	const $searchItineraryAddresses = await searchItineraryAddresses
		.fetch({
			variables: {
				input: searchAddressesInput,
			},
			blocking: true,
			event,
		})
		.catch((error) => {
			// Workaround to make sure we subscribe and unsubscribe after an error is thrown
			// This avoids a memory leak coming from the houdini store
			//
			// See https://github.com/HoudiniGraphql/houdini/issues/1294
			get(searchItineraryAddresses);

			throw error;
		});

	if (!$searchItineraryAddresses.data) {
		return;
	}

	const {
		departure,
		arrival,
		departureLocalizedHierarchies,
		arrivalLocalizedHierarchies,
		arrivalCityHierarchy,
		nearbyArrivalCitiesHierarchies,
	} = $searchItineraryAddresses.data.searchItineraryAddresses;
	const [departureLabel, arrivalLabel] = getItineraryLabels(
		addressesFromRouteParam,
		itineraryFromSearchParam,
		departureLocalizedHierarchies,
		arrivalLocalizedHierarchies,
		locale,
	);
	return {
		waypoints: [
			{ id: 0, index: 0, label: departureLabel, coordinates: departure.location },
			{ id: 1, index: 1, label: arrivalLabel, coordinates: arrival.location, isArrival: true },
		],
		departure: {
			name: departureLabel,
			countryCode: departure.countryCode,
			region: departure.region ?? undefined,
			zipCode: departure.zipCode ?? undefined,
			coordinates: departure.location,
		},
		arrival: {
			name: arrivalLabel,
			countryCode: arrival.countryCode,
			region: arrival.region ?? undefined,
			zipCode: arrival.zipCode ?? undefined,
			coordinates: arrival.location,
		},
		nearbyArrivalCitiesHierarchies,
		departureLocalizedHierarchies,
		arrivalLocalizedHierarchies,
		arrivalCityHierarchy,
	};
}

export async function getWaypointsInfo(event: LoadEvent, searchParams: SearchParams): Promise<WaypointsInfo | undefined> {
	const { locale } = await event.parent();
	const itineraryFromSearchParam = searchParams.itinerary;
	const addressesFromRouteParam = 'addresses' in event.params ? event.params.addresses : undefined;

	let waypointsInfo: WaypointsInfo = {
		waypoints: [],
		departure: {
			name: '',
			countryCode: '',
			region: undefined,
			zipCode: undefined,
			coordinates: undefined,
		},
		arrival: {
			name: '',
			countryCode: '',
			region: undefined,
			zipCode: undefined,
			coordinates: undefined,
		},
	};

	if (addressesFromRouteParam) {
		const waypointsInfoFromSearchItineraryAddresses = await getWaypointsInfoFromSearchItineraryAddresses(event, addressesFromRouteParam, itineraryFromSearchParam, locale);
		if (waypointsInfoFromSearchItineraryAddresses) {
			waypointsInfo = waypointsInfoFromSearchItineraryAddresses;
		}
	} else if (itineraryFromSearchParam) {
		const waypointsInfoFromSearchParams = getWaypointsInfoFromSearchParams(searchParams);
		if (waypointsInfoFromSearchParams) {
			waypointsInfo = waypointsInfoFromSearchParams;
		}
	}

	return waypointsInfo;
}
